import React, { createContext, useContext, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "../../setup";
import { UserModel } from "../modules/auth/models/UserModel";
import { ROLES, RoleName } from "./types";
import rolePermissionsData from "./rolePermissions.json";

/**
 * Permission Context Interface
 */
interface PermissionContextValue {
  user: UserModel | undefined;
  userRole: RoleName | undefined;
  userPermissions: string[];
  hasPermission: (requiredPermissions: string | string[]) => boolean;
  hasAnyPermission: (requiredPermissions: string[]) => boolean;
  hasAllPermissions: (requiredPermissions: string[]) => boolean;
  hasRole: (roles: RoleName | RoleName[]) => boolean;
  isFullAccess: boolean; // true for super_admin and admin
}

const PermissionContext = createContext<PermissionContextValue | undefined>(
  undefined,
);

/**
 * Permission Provider Component
 * Wraps the app and provides permission checking functionality
 * Supports both backend API and mock permissions for development
 */
export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const userFromRedux = useSelector<RootState>(
    ({ auth }) => auth.user,
    shallowEqual,
  ) as UserModel | undefined;

  const value = useMemo<PermissionContextValue>(() => {
    const user = userFromRedux;
    const userRole = user?.role?.role_name as RoleName | undefined;
    const roleId = user?.role?.role_id;

    // super_admin and admin have full access
    const isFullAccess =
      userRole === ROLES.SUPER_ADMIN || userRole === ROLES.ADMIN;

    // Get permissions from rolePermissions.json based on role_id
    let userPermissions: string[] = [];

    if (roleId !== undefined) {
      const roleConfig =
        rolePermissionsData.rolePermissions[
          roleId.toString() as keyof typeof rolePermissionsData.rolePermissions
        ];
      if (roleConfig) {
        userPermissions = roleConfig.permissions || [];
      }
    }

    /**
     * Check if user has specific permission(s)
     * For super_admin and admin, always returns true
     * For manager, checks against user's permissions array
     */
    const hasPermission = (requiredPermissions: string | string[]): boolean => {
      // Full access users bypass all checks
      if (isFullAccess) return true;

      // If no permissions required, allow access
      if (
        !requiredPermissions ||
        (Array.isArray(requiredPermissions) && requiredPermissions.length === 0)
      ) {
        return true;
      }

      // Convert single permission to array
      const permissions = Array.isArray(requiredPermissions)
        ? requiredPermissions
        : [requiredPermissions];

      // Check if user has at least one of the required permissions
      return permissions.some((permission) =>
        userPermissions.includes(permission),
      );
    };

    /**
     * Check if user has ANY of the required permissions
     */
    const hasAnyPermission = (requiredPermissions: string[]): boolean => {
      if (isFullAccess) return true;
      if (!requiredPermissions || requiredPermissions.length === 0) return true;
      return requiredPermissions.some((permission) =>
        userPermissions.includes(permission),
      );
    };

    /**
     * Check if user has ALL of the required permissions
     */
    const hasAllPermissions = (requiredPermissions: string[]): boolean => {
      if (isFullAccess) return true;
      if (!requiredPermissions || requiredPermissions.length === 0) return true;
      return requiredPermissions.every((permission) =>
        userPermissions.includes(permission),
      );
    };

    /**
     * Check if user has specific role(s)
     */
    const hasRole = (roles: RoleName | RoleName[]): boolean => {
      if (!userRole) return false;
      const roleArray = Array.isArray(roles) ? roles : [roles];
      return roleArray.includes(userRole);
    };

    return {
      user,
      userRole,
      userPermissions,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasRole,
      isFullAccess,
    };
  }, [userFromRedux, userFromRedux?.id, userFromRedux?.role?.role_id]); // Re-compute when Redux user changes

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Custom hook to access permission context
 * Must be used within PermissionProvider
 */
export const usePermissions = (): PermissionContextValue => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionProvider");
  }
  return context;
};
